import { forwardRef } from 'react'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import { chakra } from '@enechain/ecloud-designsystem'

/**
 * 大ステップ / サブステップの行内 icon-only 操作ボタン
 * (design `.ct-bigstep-head .ecl-icon-btn` / `.ct-substep-row .ecl-icon-btn`)。
 *
 * DS IconButton outline 相当: border あり + **白地** + hover で subtle 暗転。
 * 寸法は **40px 固定** (アイコン 16px)。 削除ボタンは `danger` で negative 色にする。
 *
 * [design 差異] design `admin/case-templates.html` は
 * `.btn.btn-tertiary.btn-square.btn-sm` = **32px** で、「2026-07-29: `.ecl-icon-btn`
 * 36px + icon 18px から、 他画面の並べ替えと同じ 32px + icon 16px へ統一」 という
 * 経緯も残っている。 それでも 40px にするのは、 **2026-08-19 デザイナー指摘
 * 「隣同士に並ぶボタンは input 系の height に揃える (今は height 低い)」** による。
 * この行のボタンは入力欄 (40px) と横に並ぶので、 32px だと 8px 低く沈んで見える。
 * design 側の追随は別途起票する。
 *
 * ⚠ **hover で background-color を「置換」しないこと。** design のコメントに
 * 「`.btn-tertiary:hover` は background-color を Hover/lowEmphasis に置換するため、
 * gray パネル (`.ct-substep-editor` = Surface/secondary) の上では白地が消えて
 * ボタンが背景に溶ける」 と明記されている。 白地を保ったまま overlay で暗転させる
 * (design と同じ `background-image: linear-gradient(overlay, overlay)` 方式)。
 *
 * ⚠ **ref と余剰 props を underlying の `chakra.button` へ必ず転送すること。**
 * DS `Tooltip` は `Tooltip.Trigger asChild` で子を clone し、 hover / focus の
 * ハンドラと ref を子へ渡してトリガー化する。 受け取った props を捨てると
 * **tooltip が一切出ない** (PR #5807 の codex-review 指摘)。
 */
type Props = Omit<HTMLChakraProps<'button'>, 'onClick'> & {
  ariaLabel: string
  disabled?: boolean
  /** 削除ボタン (negative 色) */
  danger?: boolean
  /**
   * 開閉トグルとして使う時の展開状態 (#5317)。 渡すと `aria-expanded` が付き、
   * 支援技術に「押すと開閉する」ことが伝わる。 通常の操作ボタンでは渡さない。
   */
  ariaExpanded?: boolean
  onClick?: () => void
  children: React.ReactNode
}

export const StepIconButton = forwardRef<HTMLButtonElement, Props>(
  function StepIconButton(
    { ariaLabel, disabled, danger, ariaExpanded, onClick, children, ...rest },
    ref,
  ) {
    return (
      <chakra.button
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        display="inline-flex"
        width="40px"
        height="40px"
        padding={0}
        color={danger ? 'Text.negativeHigh' : 'Text.highEmphasis'}
        background="Surface.primary"
        borderWidth="1px"
        borderColor="Border.midEmphasis"
        borderRadius="base"
        // 白地を保ったまま overlay で暗転させる (JSDoc の ⚠ 参照)。
        _hover={{
          backgroundImage:
            'linear-gradient({colors.Hover.lowEmphasis}, {colors.Hover.lowEmphasis})',
        }}
        _active={{
          backgroundImage:
            'linear-gradient({colors.Press.lowEmphasis}, {colors.Press.lowEmphasis})',
        }}
        _disabled={{
          opacity: 0.4,
          cursor: 'not-allowed',
          pointerEvents: 'none',
        }}
        cursor="pointer"
        transition="background-color 0.15s ease"
        // Tooltip 等が渡す hover / focus ハンドラ・aria-describedby を受け取る。
        // 自前指定より前に置き、 下の明示指定が優先されるようにする。
        {...rest}
        ref={ref}
        aria-expanded={ariaExpanded}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onClick}
        type="button"
      >
        {children}
      </chakra.button>
    )
  },
)
